// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/client';
import ClubSelector from '../components/ClubSelector';

type Step = 'account' | 'join-club';

export default function Register() {
  const [step, setStep] = useState<Step>('account');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [userToken, setUserToken] = useState('');
  const [clubId, setClubId] = useState('');
  const navigate = useNavigate();

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.email.trim() || !formData.password || !formData.name.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/users/register', {
        email: formData.email.trim(),
        name: formData.name.trim(),
        password: formData.password,
        phone: formData.phone.trim() || undefined,
      });

      setUserToken(response.data.token);
      setStep('join-club');
    } catch (err: unknown) {
      let errorMessage = 'Unable to create account. Please try again.';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as {
          response?: { data?: { message?: string }; status?: number };
        };
        const status = axiosError.response?.status;
        if (status === 400) {
          errorMessage = axiosError.response?.data?.message || 'Invalid input. Please check your information.';
        } else if (status === 409) {
          errorMessage = 'An account with this email already exists. Please sign in instead.';
        } else if (status === 429) {
          errorMessage = 'Too many registration attempts. Please try again later.';
        } else if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClub = async () => {
    if (!clubId) {
      setError('Please select a club');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await api.post('/users/me/join-club', { club_id: clubId }, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      navigate('/registration-pending', {
        state: { email: formData.email },
      });
    } catch (err: unknown) {
      let errorMessage = 'Unable to join club. Please try again.';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as {
          response?: { data?: { message?: string }; status?: number };
        };
        if (axiosError.response?.status === 409) {
          errorMessage = 'You are already a member of this club.';
        } else if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Join a club (after account creation)
  if (step === 'join-club') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 via-white to-white px-4 py-10">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-md">
          <h1 className="mb-2 text-center text-2xl font-semibold text-brand-700">
            Account Created
          </h1>
          <p className="mb-6 text-center text-sm text-slate-600">
            Join a club to get started, or skip for now.
          </p>

          <div className="space-y-4">
            <ClubSelector
              value={clubId}
              onChange={(id) => setClubId(id)}
              error={clubId === '' && error === 'Please select a club' ? error : undefined}
            />

            <div aria-live="polite">
              {error && error !== 'Please select a club' && (
                <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </p>
              )}
            </div>

            <button
              type="button"
              data-testid="join-club-submit"
              onClick={handleJoinClub}
              disabled={loading || !clubId}
              className="w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:opacity-50"
            >
              {loading ? 'Joining...' : 'Join Club'}
            </button>

            <button
              type="button"
              data-testid="skip-join-club"
              onClick={() => navigate('/login')}
              className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-200"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Create user account
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 via-white to-white px-4 py-10">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-md">
        <h1 className="mb-2 text-center text-2xl font-semibold text-brand-700">
          Create Account
        </h1>
        <p className="mb-6 text-center text-sm text-slate-600">
          Create your ClubView account
        </p>

        <form className="space-y-4" onSubmit={handleAccountSubmit} noValidate>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="name">
              Full Name *
            </label>
            <input
              id="name"
              type="text"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="John Doe"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="email">
              Email *
            </label>
            <input
              id="email"
              type="email"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              autoComplete="email"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="password">
              Password *
            </label>
            <input
              id="password"
              type="password"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              autoComplete="new-password"
              required
              minLength={8}
            />
            <p className="mt-1 text-xs text-slate-500">Must be at least 8 characters</p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="confirmPassword">
              Confirm Password *
            </label>
            <input
              id="confirmPassword"
              type="password"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              autoComplete="new-password"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="phone">
              Phone (Optional)
            </label>
            <input
              id="phone"
              type="tel"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="+353 1 234 5678"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              autoComplete="tel"
            />
          </div>

          <div aria-live="polite">
            {error && (
              <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>

          <div className="text-center text-sm">
            <span className="text-slate-600">Already have an account? </span>
            <Link to="/login" className="text-brand-600 hover:text-brand-700">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
